import type { EnrichedApproval, Run } from '@orqforge/shared';

export type RunRequestActionKind =
  | 'input'
  | 'review'
  | 'approval'
  | 'running'
  | 'output'
  | 'failed'
  | 'resume'
  | 'none';

export interface RunRequestNextAction {
  kind: RunRequestActionKind;
  label: string;
  detail: string;
}

export interface RunRequestGroup {
  id: string;
  title: string;
  rootRun: Run;
  runs: Run[];
  relatedRunCount: number;
  status: string;
  currentStage: string | null;
  nextAction: RunRequestNextAction;
  /** Best run to resume when the request needs operator intervention. */
  resumeRun: Run;
  /** Pending tool approvals tied to this request (directly or via delegated runs). */
  pendingApprovalCount?: number;
  pendingApprovals?: EnrichedApproval[];
}

const STATUS_PRIORITY = [
  'waiting_input',
  'waiting_review',
  'waiting_approval',
  'failed',
  'escalated',
  'running',
  'queued',
  'cancelled',
  'completed',
];

const RESUMABLE_STATUSES = new Set(['failed', 'cancelled', 'escalated']);

export function buildRunRequestGroups(runs: Run[]): RunRequestGroup[] {
  const byId = new Map(runs.map((run) => [run.id, run]));
  const grouped = new Map<string, Run[]>();

  for (const run of runs) {
    const rootId = findRequestRootRunId(run, byId);
    const existing = grouped.get(rootId) ?? [];
    existing.push(run);
    grouped.set(rootId, existing);
  }

  return [...grouped.entries()]
    .map(([rootId, groupRuns]) => buildRunRequestGroup(rootId, groupRuns, byId))
    .sort((a, b) => timeOf(b.rootRun.createdAt) - timeOf(a.rootRun.createdAt));
}

/** Overlay pending tool approvals onto request groups so the Runs page surfaces approval gates. */
export function applyPendingApprovals(
  groups: RunRequestGroup[],
  approvals: EnrichedApproval[],
  runs: Run[],
): RunRequestGroup[] {
  if (!approvals.length) return groups;

  const byId = new Map(runs.map((run) => [run.id, run]));
  const approvalsByRoot = new Map<string, EnrichedApproval[]>();
  for (const approval of approvals) {
    const rootId =
      approval.rootRunId ??
      findRequestRootRunId(byId.get(approval.runId) ?? ({ id: approval.runId } as Run), byId);
    const existing = approvalsByRoot.get(rootId) ?? [];
    existing.push(approval);
    approvalsByRoot.set(rootId, existing);
  }

  return groups.map((group) => {
    const pending = approvalsByRoot.get(group.id);
    if (!pending?.length) return group;

    const pendingRunIds = new Set(pending.map((approval) => approval.runId));
    const runsWithApprovalStatus = group.runs.map((run) =>
      pendingRunIds.has(run.id) && run.status !== 'waiting_approval'
        ? { ...run, status: 'waiting_approval' as const }
        : run,
    );

    const status =
      group.status === 'waiting_input' || group.status === 'waiting_review'
        ? group.status
        : 'waiting_approval';

    return {
      ...group,
      runs: runsWithApprovalStatus,
      status,
      nextAction: nextActionForStatus(status, runsWithApprovalStatus, group.rootRun),
      pendingApprovalCount: pending.length,
      pendingApprovals: pending,
    };
  });
}

function buildRunRequestGroup(
  rootId: string,
  groupRuns: Run[],
  byId: Map<string, Run>,
): RunRequestGroup {
  const rootRun = byId.get(rootId) ?? earliestRun(groupRuns);
  const orderedRuns = orderRequestRuns(rootRun, groupRuns);
  const status = rollupStatus(orderedRuns, rootRun);
  const activeRun =
    orderedRuns.find((run) => ['waiting_input', 'waiting_review', 'waiting_approval'].includes(run.status)) ??
    orderedRuns.find((run) => ['running', 'queued'].includes(run.status)) ??
    orderedRuns[orderedRuns.length - 1] ??
    rootRun;

  return {
    id: rootRun.id,
    title: summarizeRunRequestInput(rootRun.input),
    rootRun,
    runs: orderedRuns,
    relatedRunCount: orderedRuns.length,
    status,
    currentStage: activeRun.currentStage ?? rootRun.currentStage ?? null,
    nextAction: nextActionForStatus(status, orderedRuns, rootRun),
    resumeRun: findResumableRun(orderedRuns, rootRun),
  };
}

/** Run to target when resuming a request from the operator banner. */
export function findResumableRun(runs: Run[], rootRun: Run): Run {
  if (RESUMABLE_STATUSES.has(rootRun.status)) {
    return rootRun;
  }

  const child = runs
    .filter((run) => run.id !== rootRun.id && RESUMABLE_STATUSES.has(run.status))
    .sort((a, b) => timeOf(b.createdAt) - timeOf(a.createdAt))[0];

  return child ?? rootRun;
}

export function summarizeRunRequestInput(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return 'Untitled request';

  try {
    const parsed = JSON.parse(trimmed) as Record<string, unknown>;
    const vehicle = parsed['vehicle'] as Record<string, unknown> | undefined;
    if (vehicle) {
      const vehicleName = [
        vehicle['year'],
        vehicle['make'],
        vehicle['model'],
      ]
        .filter(Boolean)
        .join(' ');
      if (vehicleName) return `${vehicleName} damage assessment`;
    }

    const text = stringField(parsed, 'text') ?? stringField(parsed, 'subject');
    if (text) return truncate(text, 72);
  } catch {
    // Plain text prompts are the common path from Collaborate.
  }

  return truncate(trimmed.replace(/\s+/g, ' '), 72);
}

export function findRequestRootRunId(run: Run, byId: Map<string, Run>): string {
  let current = run;
  const seen = new Set<string>();

  while (!seen.has(current.id)) {
    seen.add(current.id);
    const parentId = current.parentRunId ?? current.resumedFromRunId ?? null;
    if (!parentId) return current.id;
    const parent = byId.get(parentId);
    if (!parent) return parentId;
    current = parent;
  }

  return run.id;
}

export function orderRequestRuns(rootRun: Run, runs: Run[]): Run[] {
  const children = runs
    .filter((run) => run.id !== rootRun.id)
    .sort((a, b) => timeOf(a.createdAt) - timeOf(b.createdAt));
  return [rootRun, ...children];
}

function earliestRun(runs: Run[]): Run {
  return [...runs].sort((a, b) => timeOf(a.createdAt) - timeOf(b.createdAt))[0];
}

export function rollupStatus(runs: Run[], rootRun: Run): string {
  // A completed orchestrator run is a completed request even when a delegated child failed.
  if (rootRun.status === 'completed') return 'completed';

  for (const status of STATUS_PRIORITY) {
    if (status === 'completed' && runs.length && runs.every((run) => run.status === 'completed')) {
      return status;
    }
    if (status !== 'completed' && runs.some((run) => run.status === status)) {
      return status;
    }
  }
  return runs[0]?.status ?? 'queued';
}

export function nextActionForStatus(
  status: string,
  runs: Run[] = [],
  rootRun?: Run,
): RunRequestNextAction {
  if (status === 'waiting_input') {
    return {
      kind: 'input',
      label: 'Answer needed',
      detail: 'Answer the open question on this request so blocked agent runs can continue.',
    };
  }
  if (status === 'waiting_review') {
    return {
      kind: 'review',
      label: 'Review outcome',
      detail: 'Open the review gate, approve the result, or request changes.',
    };
  }
  if (status === 'waiting_approval') {
    return {
      kind: 'approval',
      label: 'Approval needed',
      detail: 'A specialist is waiting for you to approve a sensitive tool call before it can continue.',
    };
  }
  if (status === 'failed' || status === 'escalated') {
    return {
      kind: 'failed',
      label: 'Needs operator',
      detail: 'Inspect the issue, then resume the request with corrected instructions.',
    };
  }
  if (status === 'running' || status === 'queued') {
    return {
      kind: 'running',
      label: 'Agents working',
      detail: 'Specialist runs are still producing the result.',
    };
  }
  if (status === 'completed') {
    const failedChildren =
      rootRun
        ? runs.filter((run) => run.id !== rootRun.id && run.status === 'failed')
        : runs.filter((run) => run.status === 'failed');
    if (failedChildren.length) {
      return {
        kind: 'output',
        label: 'Open output',
        detail: `${failedChildren.length} specialist run(s) failed, but the orchestrator completed. Review outputs below.`,
      };
    }
    return {
      kind: 'output',
      label: 'Open output',
      detail: 'Review the generated answer and downloaded files for this request.',
    };
  }
  if (status === 'cancelled') {
    return {
      kind: 'resume',
      label: 'Resume request',
      detail: 'Start a new attempt from the saved request context.',
    };
  }
  return {
    kind: 'none',
    label: 'No action',
    detail: 'No operator action is needed right now.',
  };
}

function stringField(value: Record<string, unknown>, key: string): string | null {
  const field = value[key];
  return typeof field === 'string' && field.trim() ? field.trim() : null;
}

function truncate(value: string, max: number): string {
  return value.length > max ? `${value.slice(0, max - 1)}...` : value;
}

function timeOf(value: unknown): number {
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'string' || typeof value === 'number') return new Date(value).getTime();
  return 0;
}
